import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogInner,
  DialogTitle,
} from "@/components/ui/dialog";
import { useRemoveScenarioAssignmentMutation } from "@/graphql";
import { formatDate } from "@/lib/format";

interface DeleteModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  assignmentId?: string;
  assignmentName?: string;
  participantName?: string;
  dueDate?: Date;
}

export function DeleteModal({
  open,
  onOpenChange,
  onSuccess,
  assignmentId,
  assignmentName,
  participantName,
  dueDate,
}: DeleteModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [removeAssignmentMutation] = useRemoveScenarioAssignmentMutation();

  const handleConfirm = async () => {
    if (!assignmentId) return;

    setIsDeleting(true);
    try {
      const result = await removeAssignmentMutation({
        variables: {
          assignmentId,
        },
      });

      if (result.data?.removeScenarioAssignment?.ok) {
        onSuccess?.();
        onOpenChange(false);
      } else {
        console.error(
          "Failed to delete assignment:",
          result.data?.removeScenarioAssignment?.errors,
        );
      }
    } catch (error) {
      console.error("Error deleting assignment:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[550px] border border-gray-200 rounded-lg">
        {/* Header */}
        <DialogHeader>
          <DialogTitle>Delete Assignment</DialogTitle>
        </DialogHeader>

        {/* Content */}
        <DialogInner className="space-y-3">
          {/* Warning Message */}
          <div className="text-sm text-gray-900 leading-relaxed">
            Are you sure you want to delete this assignment?
          </div>

          {assignmentName && participantName && (
            <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
              <div className="text-sm font-medium text-gray-900">
                {assignmentName}
              </div>
              <div className="text-sm text-gray-600">
                Assigned to {participantName}
              </div>
              <div className="text-sm text-gray-600">
                Due: {formatDate(dueDate)}
              </div>
            </div>
          )}

          <div className="text-sm text-gray-600 leading-relaxed">
            This action cannot be undone. The assignment will be permanently
            removed.
          </div>
        </DialogInner>
        <DialogFooter className="flex justify-end">
          <Button
            intent="danger"
            onClick={handleConfirm}
            disabled={isDeleting}
            className="w-[100px] h-10"
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
          <Button
            intent="secondary"
            variant="outline"
            onClick={handleCancel}
            disabled={isDeleting}
            className="w-[100px] h-10"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
